import apiClient from '../../api/common/apiClient.ts';
import { Endpoints } from '@qcdoc/mock-runtime/contracts/endpoints.ts';
import { mapCoaFilters } from '../../api/coa/coaApi.ts';
import type { PagedRequest, PagedResult } from '../../types/common/common.ts';
import type {
  AddVendorCoaRequest,
  CreateVendorPackageInput,
  VendorActiveVqca,
  VendorCoaExportItem,
  VendorCoaListItem,
  VendorPackageLot,
  VendorPackageSlip,
  VendorVqcaListItem,
  VendorVqcaRecord,
  VendorVqcaSignature,
  VendorVqcaSpec,
} from '../../types/vendor/vendor.types.ts';

/** Legacy ASP.NET Membership application name for the vendor portal (web.config). */
const VENDOR_APPLICATION_NAME = 'QCDOC-VENDOR';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** api/VQCA/details/{oid} returns every row for the VQCA when oid is empty. */
const ALL_DETAILS = '00000000-0000-0000-0000-000000000000';

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Serialize source vendor lots into the /Data/Vendor_Lots XML AddSlip expects. */
function buildLotXml(lots: { lotNumber: string; poNumber?: string }[]): string {
  const nodes = lots
    .filter((l) => l.lotNumber?.trim())
    .map((l) => `<Vendor_Lots><LotNumber>${xmlEscape(l.lotNumber.trim())}</LotNumber><PONumber>${xmlEscape((l.poNumber ?? '').trim())}</PONumber></Vendor_Lots>`)
    .join('');
  return nodes ? `<Data>${nodes}</Data>` : '';
}

function buildPoXml(poNumber?: string): string {
  const value = poNumber?.trim();
  return value ? `<Data><PKG_PO><PONumber>${xmlEscape(value)}</PONumber></PKG_PO></Data>` : '';
}

/** The COAResponse fields the vendor grid reads (camelCase over the wire). */
interface RawVendorCoa {
  oid: string;
  lotNumber?: string;
  prodName?: string;
  status?: number;
  statusName?: string;
  issueDate?: string;
  productionDate?: string;
}

/** The COAExportResponse fields the vendor export reads. */
interface RawVendorCoaExport {
  lotNumber?: string;
  prodName?: string;
  statusName?: string;
  issueDate?: string;
  productionDate?: string;
}

const mapVendorCoa = (row: RawVendorCoa): VendorCoaListItem => ({
  oid: row.oid,
  lotNumber: row.lotNumber ?? '',
  productName: row.prodName ?? '',
  statusId: row.status ?? 0,
  statusName: row.statusName ?? '',
  issueDate: row.issueDate ?? '',
  productionDate: row.productionDate ?? '',
});

export const vendorApi = {
  /** GET ?applicationName=&userName= → resolve the signed-in user's vendor id */
  getVendorId: (userName: string) =>
    apiClient.get<string>(Endpoints.vqca.getVendorId, {
      params: { applicationName: VENDOR_APPLICATION_NAME, userName },
    }),

  // ── COA — CoaController vendor endpoints ──

  /**
   * POST COAByVendorDTO → one server-paged page of the signed-in vendor's COAs.
   *
   * The backend returns a bare COAResponse array, so the paged shape the grid needs is
   * assembled here and the total comes from the companion count endpoint.
   */
  getCoaList: async (
    vendorId: string,
    params: PagedRequest,
  ): Promise<{ data: PagedResult<VendorCoaListItem> }> => {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;
    const filterBody = { VendorId: vendorId, ...mapCoaFilters(params.filters) };
    // The count only drives the pager, so a failing count must not blank the grid.
    const [listRes, countRes] = await Promise.allSettled([
      apiClient.post<RawVendorCoa[]>(Endpoints.vendorPortal.coaList, {
        ...filterBody,
        StartIndex: (page - 1) * pageSize,
        MaximumRows: pageSize,
        SORTBY: params.sortField,
      }),
      apiClient.post<number>(Endpoints.vendorPortal.coaCount, filterBody),
    ]);
    if (listRes.status === 'rejected') throw listRes.reason;
    const items = (listRes.value.data ?? []).map(mapVendorCoa);
    const totalCount =
      countRes.status === 'fulfilled' && typeof countRes.value.data === 'number'
        ? countRes.value.data
        : items.length;
    return {
      data: {
        items, totalCount, page, pageSize,
        totalPages: Math.ceil(totalCount / pageSize),
        hasNextPage: page * pageSize < totalCount,
        hasPreviousPage: page > 1,
      },
    };
  },

  /**
   * Every COA matching the filters, for "Export to Excel".
   *
   * The grid pages server-side, so exporting the rows already in the cache would only ever
   * write the page on screen. Paging is irrelevant here; only the filters and sort carry over.
   */
  getCoaListForExport: async (
    vendorId: string,
    params: PagedRequest,
  ): Promise<VendorCoaExportItem[]> => {
    const res = await apiClient.post<RawVendorCoaExport[]>(Endpoints.vendorPortal.coaListExport, {
      VendorId: vendorId,
      ...mapCoaFilters(params.filters),
      SORTBY: params.sortField,
    });
    return (res.data ?? []).map((row) => ({
      lotNumber: row.lotNumber ?? '',
      productName: row.prodName ?? '',
      statusName: row.statusName ?? '',
      issueDate: row.issueDate ?? '',
      productionDate: row.productionDate ?? '',
    }));
  },

  /** GET api/COA/{coaId} */
  getCoaDetail: (id: string) =>
    apiClient.get(Endpoints.vendorPortal.coaDetail(id)),

  /** POST body: AddVendorCOARequestDTO */
  createCoa: (dto: AddVendorCoaRequest) =>
    apiClient.post(Endpoints.vendorPortal.coaCreate, dto),

  /** GET ?lotNum=&vqcaId= → existing COAs with the same lot for this VQCA */
  checkCoaDuplicate: (params: { vqcaId: string; lotNumber: string }) =>
    apiClient.get<unknown[]>(Endpoints.vendorPortal.coaCheckDuplicate, {
      params: { lotNum: params.lotNumber, vqcaId: params.vqcaId },
    }),

  // ── VQCA — VQCAController vendor endpoints ──

  getVqcaRecord: (id: string) =>
    apiClient.get<VendorVqcaRecord>(`/api/VQCA/record/${encodeURIComponent(id)}`),

  /** GET api/VQCA/vendor/{vendorId} */
  getVqcaList: (vendorId: string) =>
    apiClient.get<VendorVqcaListItem[]>(Endpoints.vendorPortal.vqcaList(vendorId)),

  /** GET api/VQCA/pending/{vendorId} → awaiting this vendor's signature */
  getVqcaPending: (vendorId: string) =>
    apiClient.get<VendorVqcaListItem[]>(Endpoints.vendorPortal.vqcaPending(vendorId)),

  /** GET api/VQCA/active/{vendorId} → APPROVED VQCAs for the COA product list */
  getActiveVqcas: (vendorId: string) =>
    apiClient.get<VendorActiveVqca[]>(Endpoints.vendorPortal.vqcaActive(vendorId)),

  /** GET api/VQCA/details/{oid}?vqcaId= → specification rows for a VQCA */
  getVqcaSpecs: (vqcaId: string) =>
    apiClient.get<VendorVqcaSpec[]>(Endpoints.vendorPortal.vqcaSpecs(ALL_DETAILS), {
      params: { vqcaId },
    }),

  /** POST body: VendorSignVQCADTO → SIGNEDBYCATMGR → APPROVED */
  signVqca: (vqcaId: string) =>
    apiClient.post<boolean>(Endpoints.vendorPortal.vqcaSign, {
      VQCAId: vqcaId,
    }),

  /** GET ?VQCAId={Guid} → sign logs */
  getVqcaSignLogs: (id: string) =>
    apiClient.get<VendorVqcaSignature[]>(Endpoints.vendorPortal.vqcaSignLogs, { params: { VQCAId: id } }),

  // ── Packaging — PackagingSlipController ──

  /** POST body: PackageDTO → this vendor's packaging slips (bare array). */
  getPackagingList: async (vendorId: string, params: PagedRequest): Promise<{ data: PagedResult<VendorPackageSlip> }> => {
    const res = await apiClient.post<VendorPackageSlip[]>(Endpoints.vendorPortal.packagingList, {
      VendorId: vendorId,
      ...params.filters,
    });
    const all = Array.isArray(res.data) ? res.data : [];
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;
    const start = (page - 1) * pageSize;
    return {
      data: {
        items: all.slice(start, start + pageSize),
        totalCount: all.length,
        page,
        pageSize,
        totalPages: Math.ceil(all.length / pageSize),
        hasNextPage: page * pageSize < all.length,
        hasPreviousPage: page > 1,
      },
    };
  },

  /** GET ?OId={Guid} → packaging slip by id (endpoint returns an array). */
  getPackagingDetail: async (id: string): Promise<{ data: VendorPackageSlip | undefined }> => {
    const res = await apiClient.get<VendorPackageSlip[] | VendorPackageSlip>(Endpoints.vendorPortal.packagingDetail, { params: { OId: id } });
    const body = res.data;
    return { data: Array.isArray(body) ? body[0] : body };
  },

  /** GET ?pkgSlipId={Guid} → source vendor lots for a slip. */
  getPackagingVendorLots: (pkgSlipId: string) =>
    apiClient.get<VendorPackageLot[]>(Endpoints.vendorPortal.packagingVendorLots, { params: { pkgSlipId } }),

  /** POST body: AddSlipDTO — create a packaging slip (packager = the signed-in vendor). */
  createPackaging: (dto: CreateVendorPackageInput) =>
    apiClient.post(Endpoints.vendorPortal.packagingCreate, {
      VendorId: dto.vendorId,
      ProductId: dto.productId,
      COALotNumber: dto.packagerLot,
      COAPONumber: dto.packagerPO ?? '',
      LotXml: buildLotXml(dto.vendorLots),
      POXml: buildPoXml(dto.packagerPO),
      CurrentUser: dto.currentUser,
      PackagerId: dto.vendorId,
      PkgSlipId: EMPTY_GUID,
    }),

  /** POST body: PackageDTO — existing slips with the same product + packager lot. */
  checkPackagingDuplicate: (params: { productId: string; vendorId: string; lotNumber: string }) =>
    apiClient.post<VendorPackageSlip[]>(Endpoints.vendorPortal.packagingList, {
      VendorId: params.vendorId,
      ProductId: params.productId,
      PkgLotNumber: params.lotNumber,
    }),
};

export const vendorService = {
  getVqcaList: async (vendorId: string) => structuredClone((await vendorApi.getVqcaList(vendorId)).data),
  getVqcaPending: async (vendorId: string) => structuredClone((await vendorApi.getVqcaPending(vendorId)).data),
  getActiveVqcas: async (vendorId: string) => structuredClone((await vendorApi.getActiveVqcas(vendorId)).data),
  getVqcaSpecs: async (id: string) => structuredClone((await vendorApi.getVqcaSpecs(id)).data),
  getVqcaDocument: async (id: string) => {
    const record = await vendorApi.getVqcaRecord(id);
    const [specs, signatures] = await Promise.allSettled([vendorApi.getVqcaSpecs(id), vendorApi.getVqcaSignLogs(id)]);
    return structuredClone({
      record: record.data,
      specs: specs.status === 'fulfilled' ? specs.value.data : [],
      signatures: signatures.status === 'fulfilled' ? signatures.value.data : [],
      specsFailed: specs.status === 'rejected',
      signaturesFailed: signatures.status === 'rejected',
    });
  },
  signVqca: async (id: string) => (await vendorApi.signVqca(id)).data,
};
